import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Play, TrendingUp, TrendingDown, ClipboardList } from 'lucide-react';

interface HistRow {
  id: string;
  ran_at: string;
  contracts_analyzed: number;
  clients_analyzed: number;
  good_payer_profile: Record<string, string | number>;
  bad_payer_profile: Record<string, string | number>;
  renewal_profile: Record<string, string | number>;
  common_red_flags: string[];
  common_green_flags: string[];
  summary: string | null;
}

export default function HistoricalTab({ sellerId, unitId }: { sellerId: string; unitId: string | null }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<HistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    void load();
  }, [sellerId]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('ai_historical_analysis')
      .select('*')
      .eq('seller_id', sellerId)
      .order('ran_at', { ascending: false })
      .limit(5);
    setRows(((data ?? []) as any).map(normalize));
    setLoading(false);
  }

  function normalize(r: any): HistRow {
    return {
      id: r.id,
      ran_at: r.ran_at,
      contracts_analyzed: r.contracts_analyzed,
      clients_analyzed: r.clients_analyzed,
      good_payer_profile: r.good_payer_profile ?? {},
      bad_payer_profile: r.bad_payer_profile ?? {},
      renewal_profile: r.renewal_profile ?? {},
      common_red_flags: Array.isArray(r.common_red_flags) ? r.common_red_flags : [],
      common_green_flags: Array.isArray(r.common_green_flags) ? r.common_green_flags : [],
      summary: r.summary,
    };
  }

  async function runAnalysis() {
    if (!user) return;
    setRunning(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        (import.meta.env.VITE_SUPABASE_URL as string) + '/functions/v1/ai-historical-analysis',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ seller_id: sellerId, unit_id: unitId }),
        }
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.error || 'Falha ao rodar analise');
      await load();
    } catch (err) {
      alert('Nao foi possivel rodar a analise agora: ' + (err as Error).message);
    } finally {
      setRunning(false);
    }
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  const latest = rows[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Analise dos contratos passados</h3>
          <p className="mt-1 text-xs text-slate-500">
            O agente le todos os contratos e clientes deste vendedor e desenha o perfil de quem paga bem, quem paga mal
            e quem costuma renovar.
          </p>
        </div>
        <button
          onClick={runAnalysis}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          <Play className="h-4 w-4" />
          {running ? 'Rodando analise...' : 'Rodar analise agora'}
        </button>
      </div>

      {!latest && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Nenhuma analise gerada ainda.
        </div>
      )}

      {latest && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Ultima analise</div>
                <div className="text-sm font-medium text-slate-900">
                  {new Date(latest.ran_at).toLocaleString('pt-BR')} — {latest.contracts_analyzed} contratos /{' '}
                  {latest.clients_analyzed} clientes
                </div>
              </div>
              <ClipboardList className="h-5 w-5 text-slate-400" />
            </div>
            {latest.summary && <p className="text-sm leading-relaxed text-slate-700">{latest.summary}</p>}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <ProfileCard
              title="Bom pagador"
              tone="emerald"
              icon={TrendingUp}
              profile={latest.good_payer_profile}
            />
            <ProfileCard
              title="Mau pagador"
              tone="red"
              icon={TrendingDown}
              profile={latest.bad_payer_profile}
            />
            <ProfileCard
              title="Renova contrato"
              tone="blue"
              icon={TrendingUp}
              profile={latest.renewal_profile}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <FlagsCard title="Sinais positivos" tone="emerald" items={latest.common_green_flags} />
            <FlagsCard title="Sinais de alerta" tone="red" items={latest.common_red_flags} />
          </div>

          {rows.length > 1 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h4 className="mb-3 text-sm font-semibold text-slate-900">Analises anteriores</h4>
              <ul className="space-y-1 text-sm text-slate-600">
                {rows.slice(1).map((r) => (
                  <li key={r.id}>
                    {new Date(r.ran_at).toLocaleString('pt-BR')} — {r.contracts_analyzed} contratos
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProfileCard({
  title,
  tone,
  icon: Icon,
  profile,
}: {
  title: string;
  tone: 'emerald' | 'red' | 'blue';
  icon: React.ElementType;
  profile: Record<string, string | number>;
}) {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
  } as const;
  const entries = Object.entries(profile);
  return (
    <div className={'rounded-xl border p-4 ' + tones[tone]}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <span className="text-sm font-semibold">{title}</span>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs opacity-70">Sem dados ainda.</p>
      ) : (
        <dl className="space-y-1 text-xs">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-2">
              <dt className="opacity-70">{k}</dt>
              <dd className="text-right font-medium">{String(v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function FlagsCard({ title, tone, items }: { title: string; tone: 'emerald' | 'red'; items: string[] }) {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50/50 text-emerald-700',
    red: 'border-red-200 bg-red-50/50 text-red-700',
  } as const;
  return (
    <div className={'rounded-xl border p-4 ' + tones[tone]}>
      <div className="mb-2 text-sm font-semibold">{title}</div>
      {items.length === 0 ? (
        <p className="text-xs opacity-70">Nenhum registro.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-current" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
